import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Link as MuiLink,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const CycleFilesModule = ({ elementType, elementId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadCategory, setUploadCategory] = useState('General');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCycleFiles();
  }, [elementType, elementId]);

  const fetchCycleFiles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/ciclos/${elementType}/${elementId}/adjuntos`);
      setFiles(response.data);
    } catch (err) {
      console.error('Failed to fetch cycle files', err);
      setError('No se pudieron cargar los archivos del ciclo.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('categoria', uploadCategory);

    try {
      let endpoint = '';
      if (elementType === 'requisicion') {
        endpoint = `/api/requisiciones/${elementId}/adjuntos`;
      } else if (elementType === 'solicitud_cotizacion') {
        endpoint = `/api/solicitudes_cotizacion/${elementId}/adjuntos`;
      } else if (elementType === 'orden_compra') {
        endpoint = `/api/ordenes_compra/${elementId}/adjuntos`;
      }

      await axios.post(endpoint, formData);
      // Refresh cycle files after upload
      fetchCycleFiles();
      event.target.value = null; // Reset file input
    } catch (err) {
      setError('Error al subir el archivo.');
      console.error(err);
    }
  };

  const handleDeleteFile = async (fileId, sourceType) => {
    if (!window.confirm('¿Está seguro de que desea eliminar este archivo?')) {
      return;
    }

    try {
      let endpoint = '';
      if (sourceType === 'requisicion') {
        endpoint = `/api/requisiciones/adjuntos/${fileId}`;
      } else if (sourceType === 'solicitud_cotizacion') {
        endpoint = `/api/solicitudes_cotizacion/adjuntos/${fileId}`;
      } else if (sourceType === 'orden_compra') {
        endpoint = `/api/ordenes_compra/adjuntos/${fileId}`;
      }

      await axios.delete(endpoint);
      setFiles(files.filter((f) => f.id !== fileId));
    } catch (err) {
      setError('Error al eliminar el archivo.');
      console.error(err);
    }
  };

  const getFileUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) {
      return path;
    }
    return path;
  };

  // Group files by category
  const groupedFiles = files.reduce((acc, file) => {
    const category = file.categoria || 'Sin Categoría';
    (acc[category] = acc[category] || []).push(file);
    return acc;
  }, {});

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Archivos del Ciclo
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* Upload Section */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Categoría</InputLabel>
          <Select value={uploadCategory} label="Categoría" onChange={(e) => setUploadCategory(e.target.value)}>
            <MenuItem value="General">General</MenuItem>
            <MenuItem value="Factura">Factura</MenuItem>
            <MenuItem value="Comprobante de Pago">Comprobante de Pago</MenuItem>
            <MenuItem value="Complemento de Pago">Complemento de Pago</MenuItem>
            <MenuItem value="Nota de credito">Nota de credito</MenuItem>
            <MenuItem value="Remision">Remision</MenuItem>
            <MenuItem value="Contrato">Contrato</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={() => fileInputRef.current.click()} size="small">
          <UploadFileIcon fontSize="small" />
        </Button>
        <input type="file" hidden ref={fileInputRef} onChange={handleFileUpload} />
      </Box>

      {/* Files List Grouped by Category */}
      {Object.keys(groupedFiles).length > 0 ? (
        Object.entries(groupedFiles).map(([category, categoryFiles]) => (
          <Box key={category} sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{
                fontWeight: 'bold',
                mb: 0.5,
                fontSize: '0.85rem',
              }}
            >
              {category}
            </Typography>
            <List dense>
              {categoryFiles.map((file) => (
                <ListItem
                  key={file.id}
                  disableGutters
                  sx={{
                    bgcolor: 'background.paper',
                    mb: 0.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    px: 1,
                  }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteFile(file.id, file.source_type)}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      <MuiLink
                        href={getFileUrl(file.url_archivo)}
                        target="_blank"
                        rel="noopener"
                        sx={{ fontSize: '0.85rem' }}
                      >
                        {file.nombre_archivo}
                      </MuiLink>
                    }
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ))
      ) : (
        <Typography sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.9rem' }}>
          No hay archivos en este ciclo.
        </Typography>
      )}

      {error && (
        <Typography color="error" sx={{ mt: 1, fontSize: '0.85rem' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default CycleFilesModule;
